import {
  createMergedPolylinesFromRenderState,
  getDecimatedTrackingMarkerIndices,
  ITrackingRenderState,
  latLngTuplesToLineStringFeature,
  TLatLngTuple,
  TrackingSegmentStyleEnum,
} from "@ryde/shared";

import { ITrailSegment } from "./maplibre-tracking-route.models";

/**
 * The endpoints of every no-signal gap in the trail. Decimation is free to thin these away, but they are the
 * two samples a user actually wants to hover — the last fix before the signal dropped and the first one after
 * it — so they are added back regardless of zoom.
 */
const resolveGapEndpointIndices = (state: ITrackingRenderState): number[] => {
  const endpoints: number[] = [];

  state.segmentStyles.forEach((style, segmentIndex) => {
    if (style === TrackingSegmentStyleEnum.Dashed) {
      endpoints.push(segmentIndex, segmentIndex + 1);
    }
  });

  return endpoints;
};

/**
 * Which trail points get a hoverable dot: the zoom-decimated set plus both ends of every gap, minus the first
 * and last points (the trail start and the car marker already own those positions).
 */
export const resolveTrailMarkerIndices = ({
  state,
  zoom,
  minZoom,
}: {
  state: ITrackingRenderState;
  zoom: number;
  minZoom?: number;
}): number[] => {
  const decimated = getDecimatedTrackingMarkerIndices({
    pointCount: state.pointCount,
    zoom,
    minZoom,
  });
  const gapEndpoints = resolveGapEndpointIndices(state);

  if (!gapEndpoints.length) {
    return decimated;
  }

  return [...new Set([...decimated, ...gapEndpoints])]
    .filter(index => index > 0 && index < state.pointCount - 1)
    .sort((a, b) => a - b);
};

/**
 * The trail as drawable segments: normal GPS runs in the route colour, no-signal gaps dotted grey, detours
 * dashed red — merged so consecutive same-style points become one line instead of one per sample.
 *
 * The geometry published here is the TRUE GPS track. The sideways separation from the planned route is left to
 * MapLibre's `line-offset`, applied in screen space on the GPU: baking a pixel distance into lat/lng freezes it
 * at whatever zoom was current, and at the map's initial zoom of 7 an 8 px offset is 4.15 km per point — which
 * is what once scattered the trail into a starburst no later zoom could undo.
 *
 * Built up front rather than inline in JSX because `<Source>` deep-compares its `data` during render, so a
 * fresh literal would walk every coordinate of every segment on each re-render — and the trail re-renders on
 * every zoom.
 */
export const buildTrailSegments = ({
  state,
  routeColor,
}: {
  state: ITrackingRenderState;
  routeColor: string;
}): ITrailSegment[] =>
  createMergedPolylinesFromRenderState({ state, routeColor }).map(line => ({
    feature: latLngTuplesToLineStringFeature(line.positions as TLatLngTuple[]),
    lineColor: line.lineColor,
    dashArray: line.dashArray,
  }));
